import {Injectable} from '@angular/core';
import {GENERIC_ERROR, NETWORK_ERROR} from './assistant.constants';

/** Wire types: docs/chatbot-redesign/SSE-CONTRACT.md. */
export interface ChatSource {
  title: string;
  url: string;
}

export type ChatErrorCode =
  | 'empty_message'
  | 'message_too_long'
  | 'conversation_expired'
  | 'conversation_limit'
  | 'rate_limited'
  | 'timeout'
  | 'busy'
  | 'upstream_error'
  | 'forbidden_origin'
  | 'content_filtered'
  | 'network';

/** Reserved for tool-call confirmation; the server doesn't send it yet. */
export interface ChatActionRequest {
  actionId: string;
  kind: string;
  title: string;
  details: Array<{label: string; value: string}>;
  confirmLabel: string;
  cancelLabel: string;
  expiresAt: string;
}

export type ChatStreamEvent =
  | {type: 'status'; phase: string; text: string}
  | {type: 'delta'; text: string}
  | {type: 'sources'; sources: ChatSource[]}
  | {type: 'done'; conversation: string; messageId: string; html: string; pendingAction?: string}
  | {type: 'error'; code: ChatErrorCode; message: string; correlationId?: string}
  | {type: 'action_request'; action: ChatActionRequest}
  | {type: 'action_result'; actionId: string; status: 'done' | 'cancelled' | 'expired' | 'failed'; message: string}
  | {type: 'unknown'; name: string};

export class ChatError extends Error {
  constructor(
    readonly code: ChatErrorCode,
    message: string,
    readonly correlationId?: string,
    readonly retryAfterSeconds?: number,
  ) {
    super(message);
  }
}

export interface RawSseEvent {
  event: string;
  data: string;
}

/**
 * Incremental Server-Sent Events parser (WHATWG rules): fields split on CR, LF or CRLF, a blank
 * line dispatches, data lines join with "\n", comments and unknown fields are ignored.
 * EventSource can't POST, so the widget reads the fetch body through this.
 */
export class SseParser {
  private buffer = '';
  private event = '';
  private data: string[] = [];

  push(chunk: string): RawSseEvent[] {
    this.buffer += chunk;
    const events: RawSseEvent[] = [];

    for (;;) {
      const match = /\r\n|\r|\n/.exec(this.buffer);
      if (!match) {
        break;
      }
      // A lone CR at the very end may be the first half of CRLF; wait for more input.
      if (match[0] === '\r' && match.index === this.buffer.length - 1) {
        break;
      }

      const line = this.buffer.slice(0, match.index);
      this.buffer = this.buffer.slice(match.index + match[0].length);
      const event = this.line(line);
      if (event) {
        events.push(event);
      }
    }

    return events;
  }

  private line(line: string): RawSseEvent | null {
    if (line === '') {
      if (this.data.length === 0) {
        this.event = '';
        return null;
      }
      const event = {event: this.event || 'message', data: this.data.join('\n')};
      this.event = '';
      this.data = [];
      return event;
    }

    if (line.startsWith(':')) {
      return null;
    }

    const colon = line.indexOf(':');
    const field = colon < 0 ? line : line.slice(0, colon);
    let value = colon < 0 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) {
      value = value.slice(1);
    }

    if (field === 'event') {
      this.event = value;
    } else if (field === 'data') {
      this.data.push(value);
    }
    return null;
  }
}

/** Turns a raw event into a typed one. Malformed data becomes an upstream error. */
export function toStreamEvent(raw: RawSseEvent): ChatStreamEvent {
  let data: any;
  try {
    data = JSON.parse(raw.data);
  } catch {
    return {type: 'error', code: 'upstream_error', message: GENERIC_ERROR};
  }

  switch (raw.event) {
    case 'status':
      return {type: 'status', phase: String(data?.phase ?? ''), text: String(data?.text ?? '')};
    case 'delta':
      return {type: 'delta', text: String(data?.text ?? '')};
    case 'sources':
      return {type: 'sources', sources: Array.isArray(data) ? data : []};
    case 'done':
      return {
        type: 'done',
        conversation: String(data?.conversation ?? ''),
        messageId: String(data?.messageId ?? ''),
        html: String(data?.html ?? ''),
        pendingAction: data?.pendingAction ?? undefined,
      };
    case 'error':
      return {type: 'error', code: data?.code ?? 'upstream_error', message: String(data?.message || GENERIC_ERROR), correlationId: data?.correlationId};
    case 'action_request':
      return {type: 'action_request', action: data as ChatActionRequest};
    case 'action_result':
      return {type: 'action_result', actionId: String(data?.actionId ?? ''), status: data?.status, message: String(data?.message ?? '')};
    default:
      return {type: 'unknown', name: raw.event};
  }
}

/** Reads the chat API: POST + event stream via fetch, cancelled with an AbortSignal. */
@Injectable({providedIn: 'root'})
export class ChatStreamService {
  readonly apiBase: string;

  constructor() {
    let host = window.location.host;
    if (host === 'localhost:4200') {
      host = 'localhost:5001';
    }
    this.apiBase = `https://${host}/api/chatbot`;
  }

  /**
   * Streams one turn. Throws ChatError for HTTP errors (JSON body), network failures, and streams
   * that end without done or error. Aborting the signal rejects with the abort reason.
   */
  async *stream(path: string, body: unknown, signal: AbortSignal): AsyncGenerator<ChatStreamEvent> {
    let response: Response;
    try {
      response = await fetch(this.apiBase + path, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'text/event-stream'},
        body: JSON.stringify(body),
        credentials: 'same-origin',
        cache: 'no-store',
        signal,
      });
    } catch (error) {
      throw signal.aborted ? error : new ChatError('network', NETWORK_ERROR);
    }

    if (!response.ok || !response.body) {
      throw await errorFrom(response);
    }

    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
    const parser = new SseParser();
    let finished = false;
    try {
      for (;;) {
        let chunk: ReadableStreamReadResult<string>;
        try {
          chunk = await reader.read();
        } catch (error) {
          throw signal.aborted ? error : new ChatError('network', NETWORK_ERROR);
        }
        if (chunk.done) {
          break;
        }

        for (const raw of parser.push(chunk.value)) {
          const event = toStreamEvent(raw);
          if (event.type === 'done' || event.type === 'error') {
            finished = true;
          }
          yield event;
        }
      }
    } finally {
      reader.releaseLock();
    }

    if (!finished) {
      throw new ChatError('network', NETWORK_ERROR);
    }
  }

  /** Fire-and-forget feedback; returns whether the server accepted it. */
  async feedback(messageId: string, rating: 'up' | 'down', reason?: string): Promise<boolean> {
    try {
      const response = await fetch(this.apiBase + '/feedback', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({messageId, rating, reason: reason ?? null}),
        credentials: 'same-origin',
      });
      return response.ok;
    } catch {
      return false;
    }
  }
}

async function errorFrom(response: Response): Promise<ChatError> {
  const retryAfter = Number(response.headers.get('Retry-After'));
  try {
    const body = await response.json();
    if (body?.code && body?.message) {
      return new ChatError(body.code, body.message, body.correlationId, Number.isFinite(retryAfter) ? retryAfter : undefined);
    }
  } catch {
    // not JSON
  }
  return new ChatError('upstream_error', GENERIC_ERROR);
}
